/**
 * "Monthly limit on your AI credit" (TMMAPP-0151 K4; TMMAPI-0186 U1, MM-D-149): in the sharing
 * dialog's assistant grant row, the owner caps what one member's asks on this virtual account may use
 * of the owner's AI credit each month. No limit (`null`), an amount in the owner's AI wallet
 * currency, or Can't ask (`0`), saved through the share's `PATCH {assistantMonthlyLimitMinor}`.
 * Beside it, "{used} used this month" as served (`assistantUsedThisMonthMinor`).
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SubmitButton } from '@/components/SubmitButton';
import { formatMoneyMinor, minorToInputText, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';

type Choice = 'none' | 'amount' | 'zero';

function choiceOf(limitMinor: number | null): Choice {
  if (limitMinor === null) return 'none';
  return limitMinor === 0 ? 'zero' : 'amount';
}

interface MemberAiLimitProps {
  /** The share's id, for unique field ids. */
  shareId: number;
  /** The owner's AI wallet currency: the limit's unit. */
  currency: string;
  limitMinor: number | null;
  usedMinor: number | null;
  busy: boolean;
  /** A rejected promise keeps the draft; the dialog says why. */
  onSave: (limitMinor: number | null) => Promise<void>;
  /**
   * Whether the draft differs from the saved limit, so the dialog's guard asks before closing
   * (TMMAPP-0154 item 21). Called with `false` when the row goes away.
   */
  onUnsavedChange?: (shareId: number, unsaved: boolean) => void;
}

export function MemberAiLimit({
  shareId,
  currency,
  limitMinor,
  usedMinor,
  busy,
  onSave,
  onUnsavedChange,
}: MemberAiLimitProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const labelId = useId();
  const savedKey = `${limitMinor ?? ''}:${currency}`;
  const savedText =
    limitMinor !== null && limitMinor > 0 ? minorToInputText(limitMinor, currency, locale) : '';
  const [seenKey, setSeenKey] = useState(savedKey);
  const [choice, setChoice] = useState<Choice>(choiceOf(limitMinor));
  const [text, setText] = useState(savedText);
  const [touched, setTouched] = useState(false);
  if (seenKey !== savedKey) {
    setSeenKey(savedKey);
    setChoice(choiceOf(limitMinor));
    setText(savedText);
    setTouched(false);
  }
  const parsed = parseMoneyToMinor(text, currency, locale);
  const invalid = choice === 'amount' && (parsed === null || parsed <= 0);
  const next = choice === 'none' ? null : choice === 'zero' ? 0 : parsed;
  const dirty = next !== limitMinor;
  // Unsaved: another choice than the saved one, or an amount's text edited (even while invalid).
  const unsaved = choice !== choiceOf(limitMinor) || (choice === 'amount' && text !== savedText);
  const report = useRef(onUnsavedChange);
  useEffect(() => {
    report.current = onUnsavedChange;
  });
  useEffect(() => {
    report.current?.(shareId, unsaved);
  }, [shareId, unsaved]);
  useEffect(() => () => report.current?.(shareId, false), [shareId]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTouched(true);
    if (invalid || !dirty || busy) return;
    void onSave(next).catch(() => {
      /* the dialog says why; the draft stays */
    });
  }

  const meta = { fontSize: 12, lineHeight: '18px', color: tokens.text2 } as const;
  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      data-member-ai-limit={shareId}
      sx={{ mt: 1, paddingInlineStart: '4px' }}
    >
      <Typography id={labelId} component="p" sx={{ fontSize: 13, fontWeight: 600 }}>
        {t('assistant.share.limit.title')}
      </Typography>
      <RadioGroup
        aria-labelledby={labelId}
        value={choice}
        onChange={(event) => {
          setChoice(event.target.value as Choice);
          setTouched(false);
        }}
      >
        <FormControlLabel
          value="none"
          disabled={busy}
          control={<Radio size="small" />}
          label={<Typography sx={{ fontSize: 13 }}>{t('assistant.share.limit.none')}</Typography>}
        />
        <FormControlLabel
          value="amount"
          disabled={busy}
          control={<Radio size="small" />}
          label={<Typography sx={{ fontSize: 13 }}>{t('assistant.share.limit.amount')}</Typography>}
        />
        <FormControlLabel
          value="zero"
          disabled={busy}
          control={<Radio size="small" />}
          label={<Typography sx={{ fontSize: 13 }}>{t('assistant.share.limit.zero')}</Typography>}
        />
      </RadioGroup>
      <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mt: 0.5 }}>
        {choice === 'amount' && (
          <TextField
            size="small"
            label={t('assistant.share.limit.amountLabel', { currency })}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onBlur={() => setTouched(true)}
            error={touched && invalid}
            helperText={touched && invalid ? t('assistant.share.limit.amountInvalid') : undefined}
            disabled={busy}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            sx={{ minWidth: 160 }}
          />
        )}
        <SubmitButton
          type="submit"
          size="small"
          variant="outlined"
          loading={busy}
          disabled={!dirty}
        >
          {t('assistant.share.limit.save')}
        </SubmitButton>
      </Stack>
      {usedMinor !== null && (
        <Typography component="p" data-member-ai-used="" sx={{ ...meta, mt: '6px' }}>
          {t('assistant.share.limit.used', { used: formatMoneyMinor(usedMinor, currency, locale) })}
        </Typography>
      )}
    </Box>
  );
}
